// The Estates pages (task 3764): the Estate, its Fleets and their resources,
// and the routes the shell hands them.

import type { TranslationKey } from "@/lib/i18n";

export type EstatesUxNavItem = {
  label: string;
  /** What the row says, which `label` only identifies. */
  text: TranslationKey;
  /** The shell label a click selects. */
  target: string;
  header?: boolean;
  status?: TranslationKey;
};

// The Estate's own pages, then the Resources block for the Fleet the scope bar
// has selected. Agents and Services read the demo Estate's resources.
export const estatesUxNavRows: EstatesUxNavItem[] = [
  {
    label: "Overview",
    text: "estates.estate.nav.overview",
    target: "Estates Overview",
  },
  {
    label: "Estate Map",
    text: "estates.estate.nav.overview",
    target: "Estate Map",
  },
  { label: "Fleets", text: "estates.estate.nav.fleets", target: "Fleets" },
  {
    label: "Agents",
    text: "estates.estate.agents",
    target: "Fleet Agents",
  },
  {
    label: "Services",
    text: "estates.estate.services",
    target: "Services",
  },
];

/** The Fleets page: every Fleet in the active Estate. */
export const FLEETS_PATH = "/fleets";
/** The active Estate's own page, the sidebar's Overview. */
export const ESTATE_PATH = "/estate";
/** Estate settings, with Access as its `?tab=access`. */
export const ESTATE_SETTINGS_PATH = "/estate/settings";

/**
 * The label for an address only these pages have, so a reload lands on the
 * page rather than on Home. Neither names a Fleet, so the shell's
 * `/estate/<id>/fleet/<id>/` scope addresses never read them as an Estate id.
 */
export function estatesUxRouteInPath(pathname: string) {
  if (pathname === ESTATE_PATH)
    return { label: "Estates Overview", section: "Fleet" } as const;
  if (pathname === ESTATE_SETTINGS_PATH)
    return { label: "Estate Setup", section: "Settings" } as const;
  return null;
}

/** Labels these pages render, so a Fleet's repositories keep the address the
 * shell gives them. */
const owned = [
  "Estates Overview",
  "Fleets",
  "Repositories",
  "Fleet Agents",
  "Services",
  "Estate Setup",
  "Settings",
];

/** Whether these pages render this label's page. */
export function estatesUxOwnsRoute(label: string): boolean {
  return owned.includes(label);
}

/**
 * Whether these pages are what a label is currently rendering. The shell's
 * demo-data watermark reads from this: the shell marks these labels as demo
 * data, and these pages read the real API.
 */
export function estatesUxRendersView(label: string): boolean {
  return estatesUxOwnsRoute(label);
}

const RETURN_KEY = "autar.estatesUxRepositories";

/** Remembers the repositories page a repository is being opened from. */
export function estatesUxRememberRepositories(repositoryId: string) {
  try {
    sessionStorage.setItem(
      RETURN_KEY,
      JSON.stringify({
        repositoryId,
        path: window.location.pathname + window.location.search,
      }),
    );
  } catch {
    // Storage refused: Back falls through to Fleet Management.
  }
}

/**
 * Back from a repository's page. Opened from a Fleet's repositories page, it
 * returns there, query and all. Opened any other way, the scope
 * bar's repository choice among them, it returns to the repository's own
 * Fleet, with its way back to the Estate. The shell's own Back rebuilds the
 * Fleet's address and drops that query.
 */
export function estatesUxBack(navigate: (view: string, path?: string) => void) {
  return () => {
    let path: string | undefined;
    try {
      const saved = JSON.parse(sessionStorage.getItem(RETURN_KEY) ?? "null");
      if (saved && window.location.pathname.includes(saved.repositoryId))
        path = saved.path;
    } catch {
      // Unreadable: Back falls through to the repository's own Fleet.
    }
    navigate("Fleets", path ?? FLEETS_PATH);
  };
}
